import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchInitiativesSuccess,
    showInitiativeMessage,
    onSaveInitiativeSuccess,
    onDeleteInitiativeSuccess,
    addFavouriteInitiativeSuccess,
    onFetchSelectProductDataSuccess,
} from 'actions/initiative';
import {
    ON_SAVE_INITIATIVE,
    FETCH_ALL_INITIATIVE,
    ON_DELETE_INITIATIVE,
    ADD_FAVOURITE_INITIATIVE,
    ON_FETCH_SELECT_PRODUCT_DATA,
    ON_INITIATIVE_PRODUCT_UPDATE,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest, deleteDataRequest  } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { ORGID_PATH_PARAM } from 'constants/api';
import { mapDataToSelectionData } from 'services/selectors';

const currentOrg = (state) => state.orgs.selectedOrg;
const getInitiativeList = (state) => state.initiatives.allInitiative;
const getSelectedInitiative = (state) => state.initiatives.selectedInitiative;
const getProductList = (state) => state.products.allProduct;
const getSelectedProducts = (state) => state.initiatives.productSelectionList;

export function* fetchInitiatives() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedInitiatives = yield call(
            fetchDataRequest,
            'initiatives',
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        ) || [];
        yield put(fetchInitiativesSuccess(fetchedInitiatives));
    } catch (error) {
        yield put(showInitiativeMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteInitiative({ payload }) {
    try {
        // payload should be the initiativeId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteInitiative',
            'Deleting initiative '.concat(payload),
            [ORGID_PATH_PARAM, 'initiativeId'],
            [selectedOrg ?.id, payload]
        ) || [];
        yield put(onDeleteInitiativeSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showInitiativeMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveInitiative({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allInitiatives = yield select(getInitiativeList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload ?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg ?.id };
        }
        const savedInitiative = yield call(
            addOrUpdateDataRequest,
            allInitiatives,
            finalPayload,
            'addInitiative',
            'updateInitiative',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveInitiativeSuccess(savedInitiative));
    } catch (error) {
        yield put(showInitiativeMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteInitiative({ payload }) {
    try {
        const allInitiatives = yield select(getInitiativeList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allInitiatives.filter((entry) => entry ?.id === payload ?.id);

        if (filtered ?.length > 0) {
            const entry = filtered[0];
            const updatedInitiative = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedInitiative ?.starred).concat(' for initiative ').concat(updatedInitiative ?.id);
            yield call(
                updateDataRequest,
                updatedInitiative,
                'updateInitiative',
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteInitiativeSuccess(payload));
        }
    } catch (error) {
        yield put(showInitiativeMessage(error.messages));
    }
}

export function* onFetchSelectProductData() {
    const selectedInitiative = yield select(getSelectedInitiative);
    const allProducts = yield select(getProductList);
    const selectedDrillDownItems = (Array.isArray(selectedInitiative?.products)) ? selectedInitiative?.products : [];
    const selections = mapDataToSelectionData(allProducts, selectedDrillDownItems) || null;
    yield put(onFetchSelectProductDataSuccess(selections));
}

export function* onInitiativeProductUpdate() {
    try {
        // go through each  - check if entry is being added or removed and cleanup the other object being referenced
        const selectedOrg = yield select(currentOrg);
        const selectedInitiative = yield select(getSelectedInitiative);
        const selectedProducts = yield select(getSelectedProducts);
        const updatedDrillDownSelections = new Array();
        selectedProducts.forEach((selection) => {
            if (selection?.selected) {
                updatedDrillDownSelections.push(selection?.id);
            }
        });
        const updatedSelectedInitiative = {
            ...selectedInitiative,
            products: updatedDrillDownSelections
        };
        const auditMsg = 'Updating selected product to '.concat(' for initiative ').concat(updatedSelectedInitiative ?.id);
        yield call(
            updateDataRequest,
            updatedSelectedInitiative,
            'updateInitiative',
            auditMsg,
            [ORGID_PATH_PARAM],
            [selectedOrg.id],
        );
        yield put(onSaveInitiativeSuccess(updatedSelectedInitiative));
    } catch (error) {
        yield put(showInitiativeMessage(apiErrorMessageFormatter(error)));
    }
}

export function* fetchInitiativesSaga() {
    yield takeEvery(FETCH_ALL_INITIATIVE, fetchInitiatives);
}

export function* saveInitiativeSaga() {
    yield takeEvery(ON_SAVE_INITIATIVE, saveInitiative);
}

export function* deleteInitiativeSaga() {
    yield takeEvery(ON_DELETE_INITIATIVE, deleteInitiative);
}

export function* addFavouriteInitiativeSaga() {
    yield takeEvery(ADD_FAVOURITE_INITIATIVE, addFavouriteInitiative);
}

export function* onFetchSelectProductDataSaga() {
    yield takeEvery(ON_FETCH_SELECT_PRODUCT_DATA, onFetchSelectProductData);
}

export function* onInitiativeProductUpdateSaga() {
    yield takeEvery(ON_INITIATIVE_PRODUCT_UPDATE, onInitiativeProductUpdate);
}

export default function* rootSaga() {
    yield all([
        fork(fetchInitiativesSaga),
        fork(saveInitiativeSaga),
        fork(deleteInitiativeSaga),
        fork(addFavouriteInitiativeSaga),
        fork(onFetchSelectProductDataSaga),
        fork(onInitiativeProductUpdateSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/initiative.js